import {
  Avatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';

// relative imports
import { getSecondaryAccountLabel } from '../../../../data/app-config/configs/applicationTypeField-utilities';
import { getContextualAppPath } from '../../utilities/appUtilities';
import { createInitials } from '../../utilities/stringUtilities';
import { mapColor } from '../../utilities/styleUtilities';
import ActiveAccountIndicator from './ActiveAccountIndicator';

const VirtualizedAccountRow = ({
  index,
  style,
  data,
  currentAccount,
  chooseAccount,
  toggleDrawer,
  secondaryLabelFieldId,
  isAdminViewingClient,
  classes,
}) => {
  const navigate = useNavigate();
  const item = data[index];

  if (!item) return null;

  if (isAdminViewingClient) {
    // Client account row
    const preparerLink = item;

    const handleClientAccountClick = () => {
      toggleDrawer();
      navigate({
        to: `/client/${preparerLink.clientAccountId}`,
        resetScroll: false,
      });
    };

    return (
      <ListItemButton
        key={preparerLink.id}
        style={style}
        selected={preparerLink.clientAccountId === currentAccount.id}
        onClick={handleClientAccountClick}
        classes={{
          selected: classes.activeAccountButton,
        }}
        divider
      >
        <Avatar
          style={{
            backgroundColor: mapColor(preparerLink.clientAccountName, 90, 40),
          }}
          sx={{ mr: 1.5 }}
        >
          {createInitials(preparerLink.clientAccountName)}
        </Avatar>
        <ListItemText
          primary={preparerLink.clientAccountName}
          primaryTypographyProps={{
            noWrap: true,
            className: classes.displaySet,
          }}
          secondary={`Tax Year: ${preparerLink.clientTaxDetail.taxYear}`}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'body2',
            className: classes.accountListSecondary,
          }}
        />
        {preparerLink.clientAccountId === currentAccount.id && (
          <ListItemIcon className={classes.tealText}>
            <ActiveAccountIndicator
              circleSize={10}
              marginRight={0.5}
              text="current"
            />
          </ListItemIcon>
        )}
      </ListItemButton>
    );
  }

  // Regular account row
  const handleRegularAccountClick = () => {
    chooseAccount(item.id);
    navigate({
      to: getContextualAppPath(),
      resetScroll: false,
    });
  };

  return (
    <ListItemButton
      key={item.id}
      style={style}
      selected={item.id === currentAccount.id}
      onClick={handleRegularAccountClick}
      classes={{
        selected: classes.activeAccountButton,
      }}
      divider
    >
      <Avatar
        classes={{
          root: classes.accountAvatar,
        }}
        style={{
          backgroundColor: mapColor(item.name, 90, 40),
        }}
      >
        {createInitials(item.name)}
      </Avatar>
      <ListItemText
        primary={item.name}
        primaryTypographyProps={{
          noWrap: true,
          className: classes.displaySet,
        }}
        secondary={
          secondaryLabelFieldId
            ? getSecondaryAccountLabel(item, secondaryLabelFieldId)
            : null
        }
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'body2',
          className: classes.accountListSecondary,
        }}
      />
      {item.id === currentAccount.id && (
        <ListItemIcon className={classes.tealText}>
          <ActiveAccountIndicator
            circleSize={10}
            marginRight={0.5}
            text="current"
          />
        </ListItemIcon>
      )}
    </ListItemButton>
  );
};

VirtualizedAccountRow.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.shape().isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  currentAccount: PropTypes.shape().isRequired,
  chooseAccount: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  secondaryLabelFieldId: PropTypes.string,
  isAdminViewingClient: PropTypes.bool.isRequired,
  classes: PropTypes.shape().isRequired,
};

VirtualizedAccountRow.defaultProps = {
  secondaryLabelFieldId: null,
};

export default VirtualizedAccountRow;
